// mobileLogSheet.helpers — pure derivations + formatters for the wish
// money-log BottomSheet. Split out of `MobileLogSheetKeypad.tsx` during
// W35.5 so that file exports components only (react-refresh
// /only-export-components); these functions have no JSX and no React import,
// which is exactly what the rule asks for.
//
// Display invariant per v2 §4.5.4 W5.7-fix2: amount is always rendered as
// `Math.abs()` — the +/- UI prefix is a visual signal, never encoded into
// the storage value (the backend stores positive amount_delta).

import type { WishBlockOut } from "@/lib/api/wishlist";

export type Direction = "expense" | "income" | "both";

export interface DisplayTarget {
  /** Hard cap for the input (per DATA_MODEL §5.5: 不可超支). */
  max: number;
  /** Whether the unit is currency (mode === "amount") or percent (percent mode). */
  unit: "currency" | "percent";
  currency: string;
}

export function deriveDirection(block: WishBlockOut): Direction {
  if (block.block_type === "budget") return "expense";
  if (block.block_type === "progress") {
    const mode = (block.data?.mode as string) ?? "percent";
    return mode === "amount" ? "income" : "both";
  }
  return "expense";
}

export function isLoggable(block: WishBlockOut): boolean {
  return block.block_type === "progress" || block.block_type === "budget";
}

export function deriveTarget(block: WishBlockOut): DisplayTarget {
  if (block.block_type === "budget") {
    const amount = Number(block.data?.amount ?? 0);
    const currency = String(block.data?.currency ?? "TWD");
    return { max: amount, unit: "currency", currency };
  }
  if (block.block_type === "progress") {
    const mode = (block.data?.mode as string) ?? "percent";
    if (mode === "amount") {
      const target = Number(block.data?.target_amount ?? 0);
      const currency = String(block.data?.currency ?? "TWD");
      return { max: target, unit: "currency", currency };
    }
    return { max: 100, unit: "percent", currency: "" };
  }
  return { max: 0, unit: "percent", currency: "" };
}

export function formatCurrency(value: number, currency: string): string {
  if (!Number.isFinite(value) || value <= 0) return `$0`;
  const formatted = Math.round(value).toLocaleString();
  return `$${formatted}${currency ? ` ${currency}` : ""}`;
}

export function formatDisplay(
  value: number,
  target: DisplayTarget,
  sign: "plus" | "minus",
): string {
  const abs = Math.abs(value);
  if (target.unit === "currency") {
    const prefix = sign === "minus" ? "−" : "+";
    return `${prefix}${formatCurrency(abs, target.currency)}`;
  }
  return `${sign === "minus" ? "−" : "+"}${abs}%`;
}
